import { SHOWCASE_STORAGE_ORIGIN } from '@codeware/shared/util/csp';
import type { Media } from '@codeware/shared/util/payload-types';

/**
 * Where the example images live.
 *
 * The platform's own storage rather than a placeholder service, so the
 * showpiece does not depend on a third party staying up.
 *
 * Its own bucket rather than the seeder's: `showcase` holds imagery made for
 * these examples and nothing else, so clearing the demo tenant's stock never
 * disturbs the gallery.
 *
 * Absolute on purpose: `ImageBlock` passes an `http` url straight through,
 * which is what lets the same example render in Storybook, in Chromatic and on
 * a tenant site without any of them serving the files locally.
 *
 * The origin lives with the CSP, which allows it on every site, so a move is
 * one line there and the policy follows.
 */
const STORAGE = `${SHOWCASE_STORAGE_ORIGIN}/storage/v1/object/public`;
const SHOWCASE_BUCKET = `${STORAGE}/showcase`;

/**
 * A media document as the renderer receives one.
 *
 * Cast because a real `Media` carries the upload fields Payload fills in — id,
 * sizes, timestamps — and an example needs none of them to draw.
 */
const asMedia = (
  base: string,
  file: string,
  alt: string,
  width: number,
  height: number
): Media =>
  ({
    id: file,
    alt,
    url: `${base}/${file}`,
    width,
    height,
    mimeType: 'image/jpeg',
    updatedAt: '2026-01-01T00:00:00.000Z',
    createdAt: '2026-01-01T00:00:00.000Z'
  }) as unknown as Media;

/** A downloadable file made for these examples, beside the images. */
export const showcaseFile = (file: string): string =>
  `${SHOWCASE_BUCKET}/${file}`;

/** An image made for these examples, generated by `pnpm cdwr generate-images`. */
export const showcaseMedia = (
  file: string,
  alt: string,
  width: number,
  height: number
): Media => asMedia(SHOWCASE_BUCKET, file, alt, width, height);
